import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import { useApi } from '@/composables/useApi'
import { useAppStore } from '@/stores/app'
import type { ImportErrorSummary } from '@/interfaces/importErrorSummary'
import { onCompanyChange } from '@/stores/onCompanyChange'

const POLL_INTERVAL_MS = 60_000

export const useImportErrorsStore = defineStore('importErrors', () => {
  const appStore = useAppStore()
  const api = useApi()

  const summary: Ref<ImportErrorSummary | null> = ref(null)
  const loading = ref(false)
  let timer: ReturnType<typeof setInterval> | null = null

  const blockedImports = computed(() => summary.value?.blockedImports ?? 0)

  // Solo l'ultima richiesta può scrivere il riepilogo: il poll di A ancora in volo dopo il
  // passaggio a B, o due load ravvicinati, non sovrascrivono il dato più recente.
  let latestRequest = 0

  const load = async () => {
    const requestId = ++latestRequest
    const companyId = appStore.currentCompany?.id
    if (!companyId) {
      summary.value = null
      loading.value = false
      return
    }

    loading.value = true
    try {
      const response = await api.get('import-errors/summary', { searchParams: { company: companyId } })
      const body = (await response.json()) as ImportErrorSummary
      if (requestId !== latestRequest) return
      summary.value = body
    } catch (error) {
      if (requestId !== latestRequest) return
      // Niente toast: è anche il poll in background ogni 60 s, un errore ripetuto sarebbe rumore.
      console.error('[importErrors] Load summary failed:', error)
      summary.value = null
    } finally {
      if (requestId === latestRequest) loading.value = false
    }
  }

  onCompanyChange(() => {
    latestRequest++
    summary.value = null
    loading.value = false
  })

  const startPolling = () => {
    if (timer) return
    timer = setInterval(load, POLL_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (!timer) return
    clearInterval(timer)
    timer = null
  }

  return { summary, loading, blockedImports, load, startPolling, stopPolling }
})
